/**
 * appEnvironment — client-side detection of WHICH environment the operator is
 * looking at (Production vs Preview vs Staging vs Development).
 *
 * WHY: the single biggest operational risk once Preview and Production both
 * exist is someone looking at Preview and assuming it is Production (or vice
 * versa) — then acting on test data, or trusting a Go/No-Go banner that does
 * not reflect the live system. The EnvironmentRibbon makes the current
 * environment unmistakable on every admin/host surface.
 *
 * SAFETY:
 *   - Classification uses ONLY the browser hostname + a non-secret build_env
 *     marker from /api/health. It never reads or exposes secrets / env vars.
 *   - "Fail safe, not silent": anything we cannot POSITIVELY identify as
 *     production is treated as a cautionary, clearly-NOT-production state.
 *     We never label an unknown host "PRODUCTION".
 */

// Custom production domain(s). A build served on the custom domain is
// authoritatively production. (Set BUILD_ENV=production in the prod pod to make
// this authoritative even on a non-custom deploy URL — see useAppEnvironment.)
const PROD_HOSTS = ['japantollreceipts.jp', 'www.japantollreceipts.jp'];

export const ENVIRONMENTS = {
  production: {
    key: 'production',
    label: 'PRODUCTION',
    sub: 'Live customer data',
    icon: 'globe',
    // Solid emerald + pulse → "this is live". Text carries the meaning, not color alone.
    classes: 'bg-emerald-600 text-white ring-emerald-300',
    dot: 'bg-white',
    pulse: true,
  },
  preview: {
    key: 'preview',
    label: 'PREVIEW',
    sub: 'Test environment',
    icon: 'flask',
    classes: 'bg-amber-500 text-white ring-amber-300',
    dot: 'bg-white',
    pulse: false,
  },
  staging: {
    key: 'staging',
    label: 'STAGING',
    sub: 'Pre-release',
    icon: 'layers',
    classes: 'bg-indigo-600 text-white ring-indigo-300',
    dot: 'bg-white',
    pulse: false,
  },
  development: {
    key: 'development',
    label: 'DEVELOPMENT',
    sub: 'Local / dev',
    icon: 'wrench',
    classes: 'bg-slate-700 text-white ring-slate-400',
    dot: 'bg-white',
    pulse: false,
  },
  // Cautionary fallback — we could NOT positively confirm the environment, so
  // we explicitly do NOT claim production. Operators should verify the URL.
  unknown: {
    key: 'unknown',
    label: 'UNVERIFIED ENV',
    sub: 'Confirm the URL — not confirmed production',
    icon: 'alert',
    classes: 'bg-rose-600 text-white ring-rose-300',
    dot: 'bg-white',
    pulse: true,
  },
};

/**
 * Classify the environment from a hostname and an optional build_env marker.
 * build_env (when it is a recognised value) is authoritative; otherwise we fall
 * back to the hostname heuristic.
 */
export function classifyEnvironment(hostname = '', buildEnv = '') {
  const be = (buildEnv || '').toString().trim().toLowerCase();
  if (['production', 'prod', 'live'].includes(be)) return ENVIRONMENTS.production;
  if (['preview'].includes(be)) return ENVIRONMENTS.preview;
  if (['staging', 'stage', 'stg'].includes(be)) return ENVIRONMENTS.staging;
  if (['development', 'dev', 'local'].includes(be)) return ENVIRONMENTS.development;

  const h = (hostname || '').toString().trim().toLowerCase();
  if (!h) return ENVIRONMENTS.unknown;
  if (PROD_HOSTS.includes(h) || h.endsWith('.japantollreceipts.jp')) return ENVIRONMENTS.production;
  if (h.includes('preview.emergentagent.com')) return ENVIRONMENTS.preview;
  if (h === 'localhost' || h === '127.0.0.1' || h.endsWith('.local')) return ENVIRONMENTS.development;
  if (h.includes('staging') || h.includes('stage')) return ENVIRONMENTS.staging;
  return ENVIRONMENTS.unknown;
}

// ── Shared resolved-environment hook ──────────────────────────────────────
// Both EnvironmentRibbon and ProductionActionGuard read the SAME resolved
// environment so the badge a user sees and the guard that fires always agree.
import { useEffect, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

// One-time, session-cached read of the non-secret build_env marker from
// /api/health. When the prod pod sets BUILD_ENV=production this becomes the
// authoritative signal even on a non-custom deploy URL.
let _buildEnvCache; // undefined = not loaded, string = value (may be '')
let _buildEnvInflight = null;

export function loadBuildEnv() {
  if (_buildEnvCache !== undefined) return Promise.resolve(_buildEnvCache);
  if (_buildEnvInflight) return _buildEnvInflight;
  _buildEnvInflight = fetch(`${API}/api/health`, { credentials: 'omit' })
    .then((r) => (r.ok ? r.json() : null))
    .then((j) => {
      _buildEnvInflight = null;
      const be = (j && j.build_env) ? String(j.build_env) : '';
      // 'unknown' is a non-answer — treat as empty so the hostname heuristic wins.
      _buildEnvCache = be.toLowerCase() === 'unknown' ? '' : be;
      return _buildEnvCache;
    })
    .catch(() => { _buildEnvInflight = null; _buildEnvCache = ''; return ''; });
  return _buildEnvInflight;
}

/**
 * useResolvedEnvironment — returns the current environment config, starting
 * from the (instant, no-flash) hostname classification and refining once the
 * server's build_env marker resolves.
 */
export function useResolvedEnvironment() {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const [env, setEnv] = useState(() => classifyEnvironment(host, ''));
  useEffect(() => {
    let cancelled = false;
    loadBuildEnv().then((be) => {
      if (!cancelled && be) setEnv(classifyEnvironment(host, be));
    });
    return () => { cancelled = true; };
  }, [host]);
  return env;
}
